'use client'

import Link from 'next/link'
import { ArrowRight, Code, Zap, BookOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { analytics } from '@/lib/analytics'

export default function DevelopersPage() {
  return (
    <div className="bg-background text-charcoal">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Ship code faster.
              <br />
              <span className="text-primary">Get unstuck quicker.</span>
            </h1>
            <p className="text-lg sm:text-xl text-warm-gray mb-8 leading-relaxed">
              Debug in minutes, learn a new framework in hours, document your API in seconds. AI moves at your pace—not the opposite.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'signup', persona: 'developer' })}>
                <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-warm-gray text-charcoal hover:bg-primary/5 px-8 py-6 text-lg" asChild>
                <a href="#for-you" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'learn_more', persona: 'developer' })}>See the use cases</a>
              </Button>
            </div>
            <p className="text-sm text-warm-gray mt-4">No credit card required &middot; 5 free sessions included</p>
          </div>
        </div>
      </section>

      {/* For You Section */}
      <section id="for-you" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Made for how developers actually work</h2>
            <p className="text-warm-gray text-lg">Less time debugging. More time building.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Debug faster */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Code className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Debug faster</h3>
              <p className="text-warm-gray leading-relaxed">
                Paste an error. Get explanations, solutions, and why it happened. No more 20 minutes on Stack Overflow.
              </p>
            </div>

            {/* Learn without friction */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <BookOpen className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Learn new tech fast</h3>
              <p className="text-warm-gray leading-relaxed">
                Picking up React? Next.js? PostgreSQL? Get explanations that actually make sense, not just docs you have to parse.
              </p>
            </div>

            {/* Code review buddy */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Instant code review</h3>
              <p className="text-warm-gray leading-relaxed">
                Paste your code. Get honest feedback on edge cases, performance, and security. A second pair of eyes in seconds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Real problems. Real solutions.</h2>
            <p className="text-warm-gray text-lg">How developers are using AI to move faster</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Understand an error in seconds',
                description: 'That cryptic error message? Paste it. Get a human explanation of what went wrong, why, and how to fix it. No guessing.',
                example: 'Error: "TypeError: Cannot read property X of undefined"\nAI: Explains the bug + 3 fixes + how to prevent it'
              },
              {
                title: 'Learn a new framework by asking',
                description: 'Instead of reading docs for an hour, ask questions. "How do I do [task] in Next.js?" Get a real answer with examples.',
                example: 'You: "How do I implement middleware in Next.js?"\nAI: Explains + shows working example + common mistakes'
              },
              {
                title: 'Get code reviews without waiting',
                description: 'Paste your code. Get feedback on performance, security, edge cases, and style. Before you commit, you know if it\'s solid.',
                example: 'You: [Paste function]\nAI: Spots the edge case you missed + suggests better approach'
              },
              {
                title: 'Write boilerplate in seconds',
                description: 'Need a handler? Middleware? Test setup? Describe it once. AI generates the boring stuff so you focus on logic.',
                example: 'You: "Write a Node.js route that validates email and saves to DB"\nAI: [Complete, working code]'
              }
            ].map((useCase, i) => (
              <div key={i} className="p-8 bg-white rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-warm-gray mb-6 leading-relaxed">{useCase.description}</p>
                <div className="bg-background p-4 rounded border border-primary/10">
                  <p className="text-sm font-mono text-charcoal whitespace-pre-wrap">{useCase.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Developers are shipping faster</h2>
            <p className="text-warm-gray text-lg">See what engineering teams are doing</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: 'Saves me 2-3 hours a day on debugging and searching for answers. I can focus on actually building instead of being stuck on errors.',
                author: 'Sarah Chen',
                role: 'Full-stack engineer',
                initials: 'SC',
                avatarClass: 'bg-green-100 text-green-700',
              },
              {
                quote: 'Learning a new language is way less painful. Instead of reading docs and guessing, I just ask questions and get real answers.',
                author: 'Mike Johnson',
                role: 'Backend engineer',
                initials: 'MJ',
                avatarClass: 'bg-indigo-100 text-indigo-700',
              },
              {
                quote: 'Use it for code reviews, edge case thinking, and test generation. It catches things I would have missed. Worth it just for security review.',
                author: 'Priya Patel',
                role: 'Senior engineer',
                initials: 'PP',
                avatarClass: 'bg-orange-100 text-orange-700',
              },
            ].map((testimonial, i) => (
              <div key={i} className="p-8 bg-background rounded-lg border border-gray-200">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-warning">★</span>
                  ))}
                </div>
                <p className="text-charcoal mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.avatarClass} flex items-center justify-center text-sm font-semibold flex-shrink-0`}>
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warm-gray">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Questions from engineers</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'Is AI good enough to trust for production code?',
                a: 'As a tool? Absolutely. As a replacement for you? No. AI generates code you should review the same way you would peer review. It misses edge cases and can have security gaps. Use it to move faster, not to skip thinking.'
              },
              {
                q: 'Will this slow me down while learning?',
                a: 'No—it accelerates learning. You ask the question you would have Googled. Get an instant answer with context. You learn faster, not slower.'
              },
              {
                q: "What about my company's code privacy?",
                a: "Don't paste production code, secrets, or customer data. Use it for general programming questions and public code. Treat it like you would a conversation with a colleague."
              },
              {
                q: 'Can it handle my tech stack?',
                a: 'Try it and see. 5 free sessions means you can test with actual problems. If it helps, great. If not, you know.'
              }
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white rounded-lg border border-gray-200">
                <h3 className="font-semibold text-charcoal mb-3 text-lg">{faq.q}</h3>
                <p className="text-warm-gray leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ship code faster.</h2>
          <p className="text-lg text-warm-gray mb-8">
            Debug in minutes. Learn in hours. Build without getting stuck.
          </p>

          <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'footer_cta', cta: 'signup', persona: 'developer' })}>
            <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
